import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle, Sparkles } from "lucide-react";
import { api } from "../lib/api";

function MessageBubble({ msg }) {
    if (msg.direction === "system") {
        return (
            <div className="max-w-md mx-auto my-2">
                <div className="rounded-xl border border-[#0066FF]/30 bg-[#0066FF]/10 px-3 py-2 text-xs text-zinc-200 elate-glow">
                    <div className="flex items-center gap-1.5 text-[#5C9BFF] text-[10px] uppercase tracking-[0.18em] mb-1">
                        <Sparkles className="h-3 w-3" /> Ana
                    </div>
                    {msg.body}
                </div>
            </div>
        );
    }
    const isOutbound = msg.direction === "outbound";
    return (
        <div className={`flex ${isOutbound ? "justify-end" : "justify-start"} my-1.5`}>
            <div
                className={`max-w-[75%] px-3 py-2 text-sm rounded-2xl ${
                    isOutbound
                        ? "bg-white text-black rounded-tr-md"
                        : "bg-white/[0.04] text-white rounded-tl-md"
                }`}
            >
                {msg.body}
            </div>
        </div>
    );
}

export default function Conversations() {
    const [convos, setConvos] = useState([]);
    const [active, setActive] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/conversations").then(({ data }) => {
            setConvos(data);
            if (data.length > 0) setActive(data[0]);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, []);

    useEffect(() => {
        if (!active) return;
        api.get(`/conversations/${active.id}/messages`).then(({ data }) => {
            setMessages(data.messages || []);
        });
    }, [active]);

    return (
        <div className="space-y-6" data-testid="conversations-page">
            <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Inbox · WhatsApp</div>
                <h2 className="font-display text-3xl mt-2">Conversations</h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 min-h-[60vh]">
                {/* List */}
                <div className="elate-card p-3 overflow-y-auto max-h-[70vh]">
                    {loading ? (
                        Array.from({ length: 3 }).map((_, i) => (
                            <div key={i} className="h-16 rounded-xl bg-white/[0.02] animate-pulse mb-2" />
                        ))
                    ) : convos.length === 0 ? (
                        <div className="text-sm text-zinc-500 p-8 text-center">No conversations yet.</div>
                    ) : (
                        convos.map((c) => (
                            <button
                                key={c.id}
                                data-testid={`convo-${c.id}`}
                                onClick={() => setActive(c)}
                                className={`w-full text-left rounded-xl px-3 py-3 transition flex items-center gap-3 ${
                                    active?.id === c.id ? "bg-white/[0.06]" : "hover:bg-white/[0.03]"
                                }`}
                            >
                                <div className="h-9 w-9 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 grid place-items-center shrink-0">
                                    <MessageCircle className="h-4 w-4" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center justify-between">
                                        <div className="text-sm font-medium truncate">{c.client_name}</div>
                                        <span className="text-[10px] text-zinc-500 font-mono">WA</span>
                                    </div>
                                    <div className="text-xs text-zinc-500 truncate">{c.contact}</div>
                                </div>
                            </button>
                        ))
                    )}
                </div>

                {/* Thread */}
                <div className="lg:col-span-2 elate-card flex flex-col max-h-[70vh]">
                    {active ? (
                        <>
                            <div className="border-b border-white/[0.06] px-5 py-3 flex items-center justify-between">
                                <div>
                                    <div className="text-sm font-medium">{active.client_name}</div>
                                    <div className="text-[11px] text-zinc-500 font-mono">{active.contact} · WhatsApp</div>
                                </div>
                                <div className="text-[10px] text-zinc-500 font-mono">end-to-end relayed</div>
                            </div>
                            <div className="flex-1 overflow-y-auto px-5 py-4" data-testid="convo-messages">
                                {messages.map((m) => (
                                    <motion.div
                                        key={m.id}
                                        initial={{ opacity: 0, y: 6 }}
                                        animate={{ opacity: 1, y: 0 }}
                                    >
                                        <MessageBubble msg={m} />
                                    </motion.div>
                                ))}
                            </div>
                            <div className="border-t border-white/[0.06] px-4 py-3 text-[11px] text-zinc-500 flex items-center gap-2">
                                <Sparkles className="h-3.5 w-3.5 text-[#0066FF]" />
                                Tasks are auto-created from inbound messages by Ana.
                            </div>
                        </>
                    ) : (
                        <div className="flex-1 grid place-items-center text-sm text-zinc-500">Select a conversation.</div>
                    )}
                </div>
            </div>
        </div>
    );
}
